import {
  IJsonQuery,
  ERoomType,
  EBuildingStatus,
  EObjectType,
  EOfficeType,
  ECategoryType,
  IJsonQueryType,
} from './types/jsonQuery';
import * as R from 'ramda';

import { FDealType, FOfferType } from './types';
import { dealTypeFromJsonQuery, offerTypeFromJsonQuery } from './utils';
import { isCommercial } from './category';
import { setTerm, setTerms, setRootType, resetTerms } from './helpers';
import { setDealType } from './setDealType';
import { setRoomTypes } from './setRoomTypes';
import { DEFAULT_LONGTERM_RENT_OPTION } from './constants';

export function setOfferType(jsonQuery: IJsonQuery): (nextOfferType: FOfferType) => IJsonQuery {
  return nextOfferType => {
    let nextJsonQuery = R.clone(jsonQuery);

    const dealType = dealTypeFromJsonQuery(jsonQuery);
    const offerType = offerTypeFromJsonQuery(jsonQuery);

    if (!isCommercial(nextOfferType)) {
      nextJsonQuery = resetTerms([
        'category',
        'room',
        'object_type',
        'office_type',
        'building_status',
        'with_newobject',
      ])(nextJsonQuery);

      if (isCommercial(offerType)) {
        if (dealType & FDealType.Rent) {
          nextJsonQuery = setTerm('for_day')(nextJsonQuery)(DEFAULT_LONGTERM_RENT_OPTION);
        }
      }

      if (nextOfferType !== FOfferType.FlatNew) {
        nextJsonQuery = resetTerms(['from_developer'])(nextJsonQuery);
      }

      switch (nextOfferType) {
        case FOfferType.Flat:
          nextJsonQuery = setRootOfferType(nextJsonQuery)('flat');
          break;
        case FOfferType.FlatOld:
          nextJsonQuery = setRootOfferType(nextJsonQuery)('flat');
          nextJsonQuery = setTerm('building_status')(nextJsonQuery)(EBuildingStatus.Old);
          break;
        case FOfferType.FlatNew:
          nextJsonQuery = setRootOfferType(nextJsonQuery)('flat');
          nextJsonQuery = setTerm('building_status')(nextJsonQuery)(EBuildingStatus.New);
          break;
        case FOfferType.FlatShared:
          nextJsonQuery = setRootOfferType(nextJsonQuery)('flat');
          nextJsonQuery = setRoomTypes(jsonQuery)([ERoomType.FlatShared]);
          break;
        case FOfferType.Room:
          nextJsonQuery = setRootOfferType(nextJsonQuery)('flat');
          nextJsonQuery = setRoomTypes(jsonQuery)([ERoomType.Room]);
          break;
        case FOfferType.Bed:
          nextJsonQuery = setRootOfferType(nextJsonQuery)('flat');
          nextJsonQuery = setRoomTypes(jsonQuery)([ERoomType.Bed]);
          break;
        case FOfferType.House:
          nextJsonQuery = setRootOfferType(nextJsonQuery)('suburban');
          nextJsonQuery = setTerms('object_type')(jsonQuery)([EObjectType.House]);
          break;
        case FOfferType.Townhouse:
          nextJsonQuery = setRootOfferType(nextJsonQuery)('suburban');
          nextJsonQuery = setTerms('object_type')(jsonQuery)([EObjectType.Townhouse]);
          break;
        case FOfferType.HousePart:
          nextJsonQuery = setRootOfferType(nextJsonQuery)('suburban');
          nextJsonQuery = setTerms('object_type')(jsonQuery)([EObjectType.Housepart]);
          break;
        case FOfferType.Land:
          nextJsonQuery = setDealType(nextJsonQuery)(FDealType.Sale);
          nextJsonQuery = setRootOfferType(nextJsonQuery)('suburban');
          nextJsonQuery = setTerms('object_type')(jsonQuery)([EObjectType.Land]);
          break;
        default:
      }
    } else {
      nextJsonQuery = resetTerms(['category', 'room', 'object_type', 'building_status'])(nextJsonQuery);

      if (
        (nextOfferType &
          (FOfferType.Office | FOfferType.Warehouse | FOfferType.TradeArea | FOfferType.FreeAppointmentObject)) ===
        0
      ) {
        nextJsonQuery = setTerm('from_offrep')(nextJsonQuery)(null);
      }

      switch (nextOfferType) {
        case FOfferType.Office:
          nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = setTerms('office_type')(jsonQuery)([EOfficeType.Office]);
          break;
        case FOfferType.TradeArea:
          nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = setTerms('office_type')(jsonQuery)([EOfficeType.TradeArea]);
          break;
        case FOfferType.Warehouse:
          nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = setTerms('office_type')(jsonQuery)([EOfficeType.Warehouse]);
          break;
        case FOfferType.FreeAppointmentObject:
          nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = setTerms('office_type')(jsonQuery)([EOfficeType.FreeAppointmentObject]);
          break;
        case FOfferType.PublicCatering:
          nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = setTerms('office_type')(jsonQuery)([EOfficeType.PublicCatering]);
          break;
        case FOfferType.Garage:
          nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = setTerms('office_type')(jsonQuery)([EOfficeType.Garage]);
          break;
        case FOfferType.Manufacture:
          nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = setTerms('office_type')(jsonQuery)([EOfficeType.Manufacture]);
          break;
        case FOfferType.AutoService:
          nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = setTerms('office_type')(jsonQuery)([EOfficeType.AutoService]);
          break;
        case FOfferType.Business:
          nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = setTerms('office_type')(jsonQuery)([EOfficeType.Business]);
          break;
        case FOfferType.Building:
          nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = setTerms('office_type')(jsonQuery)([EOfficeType.Building]);
          break;
        case FOfferType.DomesticServices:
          nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = setTerms('office_type')(jsonQuery)([EOfficeType.DomesticServices]);
          break;
        case FOfferType.CommercialLand:
          nextJsonQuery = setRootOfferType(nextJsonQuery)('commercial');
          nextJsonQuery = setTerm('office_type')(nextJsonQuery)(null);
          if (dealType & FDealType.Rent) {
            nextJsonQuery = setTerms('category')(nextJsonQuery)([ECategoryType.CommercialLandRent]);
          } else if (dealType & FDealType.Sale) {
            nextJsonQuery = setTerms('category')(nextJsonQuery)([ECategoryType.CommercialLandSale]);
          }
          break;
        default:
      }
    }

    return nextJsonQuery;
  };
}

function setRootOfferType(jsonQuery: IJsonQuery) {
  return (offerType: 'flat' | 'commercial' | 'suburban' | 'newobject'): IJsonQuery => {
    const currentType = jsonQuery._type;

    const nextType: IJsonQueryType = currentType.replace(
      /flat|commercial|suburban|newobject/,
      offerType,
    ) as IJsonQueryType;

    return setRootType(nextType)(jsonQuery);
  };
}
